import {PayloadAction, createSlice} from '@reduxjs/toolkit'

import {
  ContactModalOption,
  DefaultProductionTimes,
  Discount,
  DiscountType,
  DynamicProductionTime,
  DynamicProductionTimeId,
  FinishGroupId,
  MaterialGroupId,
  ModalConfig,
  ModalConfigOpened,
  ModalContentType,
  Option,
  OptionWithLinkedProviderInfos,
  Price,
  PriceId,
  ProviderEmail,
  ProviderId,
  ProviderInfo,
  Shipping,
} from '../../types'

import {Order, OrderRefund} from '../../order-types'

export type ModalState = {
  isOpen: boolean
  modalConfig: ModalConfig
}

const initialState: ModalState = {
  isOpen: false,
  modalConfig: null,
}

export const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<ModalConfigOpened>) => {
      state.isOpen = true
      state.modalConfig = action.payload
    },
    closeModal: () => {
      return initialState
    },
  },
})

export const {openModal, closeModal} = modalSlice.actions

export type FatalErrorModalActionPayload = {
  error: Error
}

export const openFatalErrorModal = (contentProps: FatalErrorModalActionPayload) =>
  openModal({
    isCloseable: false,
    contentType: ModalContentType.FATAL_ERROR,
    contentProps,
  })

export const openChangePasswordModal = () =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.AUTH_CHANGE_PASSWORD,
    contentProps: {},
  })

export type CreatePricingModalActionPayload = {
  onDone?: (price: Price) => void
}

export const openCreatePricingModal = (contentProps: CreatePricingModalActionPayload = {}) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CREATE_PRICING,
    contentProps,
  })

export type CreateDynamicProductionTimeModalActionPayload = {
  onDone?: (dynamicProductionTime: DynamicProductionTime) => void
}

export const openCreateDynamicProductionTimeModal = (
  contentProps: CreateDynamicProductionTimeModalActionPayload = {}
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CREATE_DYNAMIC_PRODUCTION_TIME,
    contentProps,
  })

export type EditDiscountsModalActionPayload = {
  type: DiscountType
  discounts?: Discount[]
  onDone?: () => void
}

export const openEditDiscountsModal = (contentProps: EditDiscountsModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.EDIT_DISCOUNTS,
    contentProps: {
      ...contentProps,
      discounts: contentProps.discounts || [],
    },
  })

export type CreateShippingsModalActionPayload = {
  onDone?: () => void
}

export const openCreateShippingsModal = (contentProps: CreateShippingsModalActionPayload = {}) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CREATE_SHIPPINGS,
    contentProps,
  })

export type EditShippingsModalActionPayload = {
  shippings: Shipping[]
  onDone?: () => void
}

export const openEditShippingsModal = (contentProps: EditShippingsModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.EDIT_SHIPPINGS,
    contentProps,
  })

export type CreateInfillModalActionPayload = {
  onDone?: (infill: Option) => void
}

export const openCreateInfillModal = (contentProps: CreateInfillModalActionPayload = {}) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CREATE_INFILL,
    contentProps,
  })

export type EditInfillsModalActionPayload = {
  infills: OptionWithLinkedProviderInfos[]
  onDone?: () => void
}

export const openEditInfillsModal = (contentProps: EditInfillsModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.EDIT_INFILLS,
    contentProps,
  })

export type CreateToleranceModalActionPayload = {
  onDone?: (tolerance: Option) => void
}

export const openCreateToleranceModal = (contentProps: CreateToleranceModalActionPayload = {}) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CREATE_TOLERANCE,
    contentProps,
  })

export type EditTolerancesModalActionPayload = {
  tolerances: OptionWithLinkedProviderInfos[]
  onDone?: () => void
}

export const openEditTolerancesModal = (contentProps: EditTolerancesModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.EDIT_TOLERANCES,
    contentProps,
  })

export type CreateProductionTimeModalActionPayload = {
  linkedConfigsCount?: number
  defaultProductionTimes?: DefaultProductionTimes
  onDone?: (productionTime: Option) => void
}

export const openCreateProductionTimeModal = (
  contentProps: CreateProductionTimeModalActionPayload = {}
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CREATE_PRODUCTION_TIME,
    contentProps,
  })

export type EditProductionTimesModalActionPayload = {
  productionTimeOptions: OptionWithLinkedProviderInfos[]
  linkedConfigsCount: number
  defaultProductionTimes?: DefaultProductionTimes
  onDone?: () => void
}

export const openEditProductionTimesModal = (contentProps: EditProductionTimesModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.EDIT_PRODUCTION_TIMES,
    contentProps,
  })

export type MaterialModalActionPayload = {
  materialId: MaterialGroupId
}

export const openMaterialModal = (contentProps: MaterialModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.MATERIAL,
    contentProps,
  })

export type FinishGroupModalActionPayload = {
  finishGroupId: FinishGroupId
}

export const openFinishGroupModal = (contentProps: FinishGroupModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.FINISH_GROUP,
    contentProps,
  })

export type EditProviderEmailsModalActionPayload = {
  providerEmails: ProviderEmail[]
  onDone?: () => void
}

export const openEditProviderEmailsModal = (contentProps: EditProviderEmailsModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.EDIT_PROVIDER_EMAILS,
    contentProps,
  })

export type ConfirmDeleteModalActionPayload = {
  itemsToDeleteCount: number
  linkedConfigsCount?: number
  onConfirm: () => void
}

export const openConfirmDeleteModal = (contentProps: ConfirmDeleteModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CONFIRM_DELETE,
    contentProps,
  })

export type ConfirmToggleAvailabilityModalActionPayload = {
  enable: boolean
  onConfirm: () => void
}

export const openConfirmToggleAvailabilityModal = (
  contentProps: ConfirmToggleAvailabilityModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CONFIRM_TOGGLE_AVAILABILITY,
    contentProps,
  })

export type AddProviderInfoDimensionsModalActionPayload = {
  providerInfos: ProviderInfo[]
  onDone?: () => void
}

export const openAddProviderInfoDimensionsModal = (
  contentProps: AddProviderInfoDimensionsModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_DIMENSIONS,
    contentProps,
  })

export type AddProviderInfoProductionTimesModalActionPayload = {
  providerId: ProviderId
  providerInfos: ProviderInfo[]
  onDone?: () => void
}

export const openAddProviderInfoProductionTimesModal = (
  contentProps: AddProviderInfoProductionTimesModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_PRODUCTION_TIMES,
    contentProps,
  })

export type ReopenAddProviderInfoProductionTimesModalActionPayload = {
  providerId: ProviderId
  providerInfos: ProviderInfo[]
  prefilledInitialValues?: {
    productionTimeFast: string
    productionTimeSlow: string
    productionTimeIds: string[]
  }
  onDone?: () => void
}

export const reopenAddProviderInfoProductionTimesModal = (
  contentProps: ReopenAddProviderInfoProductionTimesModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_PRODUCTION_TIMES,
    contentProps,
  })

export type AddProviderInfoPricingModalActionPayload = {
  providerId: ProviderId
  providerInfos: ProviderInfo[]
  initialPriceId?: PriceId
  onDone?: () => void
}

export const openAddProviderInfoPricingModal = (
  contentProps: AddProviderInfoPricingModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_PRICING,
    contentProps,
  })

export type AddDynamicProductionTimeModalActionPayload = {
  providerId: ProviderId
  providerInfos: ProviderInfo[]
  initialDynamicProductionTimeId?: DynamicProductionTimeId
  onDone?: () => void
}

export const openAddDynamicProductionTimeModal = (
  contentProps: AddDynamicProductionTimeModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_DYNAMIC_PRODUCTION_TIME,
    contentProps,
  })

export type AddProviderInfoPrintingMethodModalActionPayload = {
  providerInfos: ProviderInfo[]
  onDone?: () => void
}

export const openAddProviderInfoPrintingMethodModal = (
  contentProps: AddProviderInfoPrintingMethodModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_PRINTING_METHOD,
    contentProps,
  })

export type ProviderInfoSuggestImmediateEditingModalActionPayload = {
  count: number
  onConfirm: () => void
  onCancel: () => void
}

export const openProviderInfoSuggestImmediateEditingModal = (
  contentProps: ProviderInfoSuggestImmediateEditingModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.PROVIDER_INFO_SUGGEST_IMMEDIATE_EDITING,
    contentProps,
  })

export type ContactModalActionPayload = {
  contactOptions?: ContactModalOption[]
}

export const openContactModal = (contentProps: ContactModalActionPayload = {}) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CONTACT,
    contentProps,
  })

export const openContactFormModal = () =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CONTACT_FORM,
    contentProps: {},
  })

export type ConfirmDeleteMessagesModalActionPayload = {
  onConfirm: () => void
}

export const openConfirmDeleteMessagesModal = (
  contentProps: ConfirmDeleteMessagesModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.CONFIRM_DELETE_MESSAGE,
    contentProps,
  })

export type SimpleContentModalActionPayload = {
  title: string
  content: React.ReactNode
  warning?: boolean
}

export const openSimpleContentModal = (contentProps: SimpleContentModalActionPayload) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.SIMPLE_CONTENT,
    contentProps: {
      ...contentProps,
      warning: contentProps.warning ?? false,
    },
  })

export type SimpleConfirmModalActionPayload = {
  title: string
  content: React.ReactNode
  onConfirm: () => void
  confirmButtonLabel?: string
}

export const openSimpleConfirmModal = (contentProps: SimpleConfirmModalActionPayload) =>
  openModal({
    isCloseable: false,
    contentType: ModalContentType.SIMPLE_CONFIRM,
    contentProps,
  })

export type AdditionalCostModalActionPayload = {
  order: Order
  providerId: ProviderId
  onDone: () => Promise<void>
}

export const openAdditionalCostModal = (contentProps: AdditionalCostModalActionPayload) =>
  openModal({
    isCloseable: false,
    contentType: ModalContentType.ADDITIONAL_COST,
    contentProps,
  })

export type LoadingModalActionPayload = {
  message: string
}

export const openLoadingModal = (contentProps: LoadingModalActionPayload) =>
  openModal({
    isCloseable: false,
    contentType: ModalContentType.LOADING,
    contentProps,
  })

export type AddTrackingNumberModalActionPayload = {
  order: Order
  onDone: (trackingNumber?: string) => void
}

export const openAddTrackingNumberModal = (contentProps: AddTrackingNumberModalActionPayload) =>
  openModal({
    isCloseable: false,
    contentType: ModalContentType.ENFORCE_TRACKING,
    contentProps,
  })

export type RequestRefundModalActionPayload = {
  order: Order
  providerId: ProviderId
  onConfirm: () => void
}

export const openRequestRefundModal = (contentProps: RequestRefundModalActionPayload) =>
  openModal({
    isCloseable: false,
    contentType: ModalContentType.REQUEST_REFUND,
    contentProps,
  })

export type PartialRefundDetailsModalActionPayload = {
  order: Order
  refund: OrderRefund
}

export const openPartialRefundDetailsModal = (
  contentProps: PartialRefundDetailsModalActionPayload
) =>
  openModal({
    isCloseable: true,
    contentType: ModalContentType.PARTIAL_REFUND_DETAILS,
    contentProps,
  })

export type ApiKeyModalActionPayload = {
  onError?: (error: string) => void
  onSuccess?: () => void
}

export const openApiKeyModal = (contentProps: ApiKeyModalActionPayload = {}) =>
  openModal({
    isCloseable: false,
    contentType: ModalContentType.API_KEY,
    contentProps,
  })

export default modalSlice.reducer
